import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MaintenanceService } from './maintenance/maintenance.service';
import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { filter } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class LogUpdateService {
  private updates = inject(SwUpdate);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly readyVersion = signal<string | null>(null);
  private readonly dismissedVersion = signal<string | null>(null);
  private checking = false;
  readonly reloading = signal(false);
  readonly updateAvailable = computed(
    () => this.readyVersion() !== null && this.readyVersion() !== this.dismissedVersion()
  );
  private static readonly UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
  private maintenance = inject(MaintenanceService);
  private router = inject(Router);

  constructor() {
    const updates = this.updates;

    if (!updates.isEnabled) {
      return;
    }

    this.handleVersionUpdates(updates);
    this.handleUnrecoverableErrors(updates);
    this.pollForUpdates(updates);
    this.handleChunkLoadErrors();
    this.clearChunkReloadGuardOnSuccess();
  }

  private handleVersionUpdates(updates: SwUpdate): void {
    updates.versionUpdates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: VersionEvent) => {
      if (event.type === 'VERSION_READY') {
        this.readyVersion.set(event.latestVersion.hash);
      }
    });
  }

  private handleUnrecoverableErrors(updates: SwUpdate): void {
    updates.unrecoverable.subscribe(() => {
      // Force a full reload to recover from a broken SW cache state.
      if (!this.maintenance.blocked()) {
        document.location.reload();
      }
    });
  }

  dismissUpdate(): void {
    this.dismissedVersion.set(this.readyVersion());
  }

  reloadForUpdate(): void {
    if (!this.updateAvailable() || this.maintenance.blocked() || this.reloading()) {
      return;
    }
    this.reloading.set(true);
    // A full reload selects the ready version without mixing old and new chunks.
    this.document.location.reload();
  }

  private pollForUpdates(updates: SwUpdate): void {
    const check = async (): Promise<void> => {
      if (this.checking) {
        return;
      }
      this.checking = true;
      try {
        await updates.checkForUpdate();
      } catch {
        // Offline checks are retried on the next poll or foreground return.
      } finally {
        this.checking = false;
      }
    };
    void check();
    const interval = setInterval(() => void check(), LogUpdateService.UPDATE_CHECK_INTERVAL_MS);
    const onVisibilityChange = (): void => {
      if (this.document.visibilityState === 'visible') {
        void check();
      }
    };
    this.document.addEventListener('visibilitychange', onVisibilityChange);
    this.destroyRef.onDestroy(() => {
      clearInterval(interval);
      this.document.removeEventListener('visibilitychange', onVisibilityChange);
    });
  }

  /**
   * Catch ChunkLoadError — happens when the user navigates to a lazy route
   * after a deploy has replaced the old chunk hashes on the server.
   * Angular Router catches rejected lazy-route imports and forwards them to
   * withNavigationErrorHandler, so we must listen there rather than on the
   * window error event (which does not receive those router-level errors).
   * A single automatic reload is enough: the SW will have activated the new
   * version by then, so the fresh index.html will reference the correct chunks.
   */
  private handleChunkLoadErrors(): void {
    this.router.events.pipe(filter((e): e is NavigationError => e instanceof NavigationError)).subscribe((e) => {
      const msg: string = e.error?.message ?? String(e.error ?? '');
      if (
        msg.includes('ChunkLoadError') ||
        msg.includes('Loading chunk') ||
        msg.includes('Failed to fetch dynamically imported module')
      ) {
        if (!this.maintenance.blocked() && !sessionStorage.getItem('chunk-reload')) {
          sessionStorage.setItem('chunk-reload', '1');
          document.location.reload();
        }
      }
    });
  }

  /**
   * Once the app boots successfully after a chunk-reload, clear the guard so
   * that a second deployment later in the same tab can trigger another reload
   * if needed.
   */
  private clearChunkReloadGuardOnSuccess(): void {
    if (sessionStorage.getItem('chunk-reload')) {
      sessionStorage.removeItem('chunk-reload');
    }
  }
}
